// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductDetailsInformationsComponent from "@store/Library/Components/ProductDetails/ProductDetailsInformations";
import ProductDetailsBundle from "@store/Overrides/Components/ProductDetails/ProductDetailsBundle";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useContext } from "react";
import { css } from "styled-components";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import RequestPackagingAdviceButton from "@store/Library/Components/RequestPackagingAdviceButton";
import {
    useIsPackagingAdviceEnabled,
    useIsSimpleRequestQuoteEnabled,
} from "@store/Framework/Store/Context/ContextHooks";
import RequestQuoteButton from "@store/Library/Components/RequestQuoteButton";

const ProductDescription = getWidgetComponent("ProductDetails/ProductDetailsDescription");

const enum fields {
    termsAndConditionsLink = "termsAndConditionsLink",
    overrideLinkLabel = "overrideLinkLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsAndConditionsLink]: LinkFieldValue;
        [fields.overrideLinkLabel]: string;
    };
}

export interface StoreProductDetailsInformationsStyle {
    descriptionWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
}

const styles: StoreProductDetailsInformationsStyle = {
    descriptionWrapper: {
        css: css`
            ${x => spacingStyles(pxToRem(16), x.theme.design.product.detail.contentSpacing)}
            display: flex;
            flex-direction: column;
            width: 100%;

            &:first-child {
                & > section {
                    margin-bottom: ${pxToRem(15)};
                    div > span {
                        font-weight: bold;
                    }
                    span {
                        font-size: ${pxToRem(24)};
                    }
                }
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(24)};
            margin-bottom: ${pxToRem(30)};
        `,
    },
};

const ProductDetailsInformations: React.FC<OwnProps> = ({ fields: { termsAndConditionsLink, overrideLinkLabel } }) => {
    const {
        product,
        product: { cantBuy },
    } = useContext(ProductContext);
    const productNotInCatalog = getBooleanProperty(product.properties.notInCatalog);
    const isPackagingAdviceEnabled = useIsPackagingAdviceEnabled();
    const isSimpleRequestQuoteEnabled = useIsSimpleRequestQuoteEnabled();

    return (
        <>
            {productNotInCatalog || cantBuy ? (
                <>
                    <StyledWrapper {...styles.descriptionWrapper}>
                        <ProductDescription />
                    </StyledWrapper>
                    {(isSimpleRequestQuoteEnabled || isPackagingAdviceEnabled) && (
                        <StyledWrapper {...styles.actionsWrapper}>
                            {isSimpleRequestQuoteEnabled && <RequestQuoteButton productId={product.id} />}
                            {isPackagingAdviceEnabled && <RequestPackagingAdviceButton />}
                        </StyledWrapper>
                    )}
                    <ProductDetailsBundle />
                </>
            ) : (
                <ProductDetailsInformationsComponent
                    termsAndConditionsLink={termsAndConditionsLink}
                    overrideLinkLabel={overrideLinkLabel}
                />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsInformations,
    definition: {
        group: "Product Details",
        displayName: "Product Details Informations",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.termsAndConditionsLink,
                displayName: "Delivery Terms T&C Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                name: fields.overrideLinkLabel,
                displayName: "Override T&C Link Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
